import {Component, Input} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import * as JsBarcode from 'jsbarcode';
import {jsPDF} from 'jspdf';
import {ILabelDesign, ILabelVariable, ITestLabelData} from '../../models/label-interfaces';
import {addLabelPdfPage, buildLabelPdfDataUri, createLabelPdfDocument} from '../../utils/label-pdf-page.utils';

type TextAlignType = 'left' | 'center' | 'right' | 'justify';

@Component({
  selector: 'app-label-printer',
  templateUrl: './label-printer.component.html',
  styleUrls: ['./label-printer.component.scss'],
  standalone: false,
})
export class LabelPrinterComponent {
  public lastGeneratedPdfDataUri: string | null = null;
  public generationProgress = 0;
  public isGenerating = false;

  public generationError = false;
  public generationErrorMessage = '';
  public lastGenerationAttempt: ITestLabelData[] | null = null;

  @Input() design!: ILabelDesign;
  @Input() data: ITestLabelData[] = [];
  private readonly dpi = 96;
  private readonly mmToPixels = this.dpi / 25.4;
  private logoCache: Map<string, HTMLImageElement> = new Map<string, HTMLImageElement>();
  private barcodeCache = new Map<string, string>();

  constructor(private sanitizer: DomSanitizer) {}

  async printLabels(): Promise<void> {
    if (!this.design || !this.data || this.data.length === 0) {
      this.showError('Missing design or data for printing');
      return;
    }

    this.lastGenerationAttempt = [...this.data];
    this.clearError();

    try {
      this.isGenerating = true;
      this.generationProgress = 0;
      const pdf = await this.generatePDF(this.data);

      this.lastGeneratedPdfDataUri = buildLabelPdfDataUri(pdf);
    } catch (error) {
      console.error('Error generating PDF:', error);
      this.showError(error instanceof Error ? error.message : 'Failed to generate PDF');
    } finally {
      this.isGenerating = false;
      this.generationProgress = 100;
    }
  }

  getLastGeneratedPdfDataUri(): string {
    return this.lastGeneratedPdfDataUri || '';
  }

  showError(message: string): void {
    this.generationError = true;
    this.generationErrorMessage = message || 'An error occurred while generating the PDF';
    this.isGenerating = false;
  }

  clearError(): void {
    this.generationError = false;
    this.generationErrorMessage = '';
  }

  retryGeneration(): void {
    this.clearError();
    if (this.lastGenerationAttempt && this.lastGenerationAttempt.length > 0) {
      void this.printLabels();
    } else {
      this.showError('No data available for retry');
    }
  }

  public closePdfPreview(): void {
    this.lastGeneratedPdfDataUri = null;
  }

  public downloadPdf(): void {
    if (!this.lastGeneratedPdfDataUri) return;

    const link = document.createElement('a');
    link.href = this.lastGeneratedPdfDataUri;
    link.download = `Labels-${new Date().toISOString().split('T')[0]}.pdf`;
    link.click();
  }

  async generatePDF(items: ITestLabelData[]): Promise<jsPDF> {
    const pdf = createLabelPdfDocument(this.design);

    try {
      await this.preCacheResources(items);
      this.generationProgress = 10;
      const totalItems = items.length;
      const BATCH_SIZE = Math.min(50, totalItems);

      // eslint-disable-next-line @typescript-eslint/no-empty-function
      pdf.internal.events.subscribe('addPage', function() {
      });

      for (let i = 0; i < items.length; i += BATCH_SIZE) {
        const startIdx = i;
        const endIdx = Math.min(i + BATCH_SIZE, items.length);
        const batch = items.slice(startIdx, endIdx);

        await Promise.all(batch.map(async (item) => {
          if (item.barcode && !this.barcodeCache.has(item.barcode)) {
            try {
              await this.generateBarcodeImage(item.barcode);
            } catch (err) {
              console.error('Error generating barcode:', err);
            }
          }
          if (item.logoUrl && !this.logoCache.has(item.logoUrl)) {
            try {
              await this.loadLogoImage(item.logoUrl);
            } catch (err) {
              console.error('Error loading logo:', err);
            }
          }
        }));

        for (let j = 0; j < batch.length; j++) {
          try {
            if (i + j > 0) {
              addLabelPdfPage(pdf, this.design);
            }

            await this.drawLabelDirectlyToPdf(pdf, batch[j]);

            this.generationProgress = Math.min(95, 10 + ((i + j + 1) / totalItems) * 85);
          } catch (labelError) {
            console.error('Error generating label:', labelError);

            pdf.setFontSize(12);
            pdf.setTextColor(255, 0, 0);
            pdf.text(
              'Error generating label: ' + (labelError.message || 'Unknown error'),
              5,
              10,
              {maxWidth: this.design.labelWidthMm - 10},
            );
          }
        }
      }

      this.generationProgress = 100;
      return pdf;
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  private async drawLabelDirectlyToPdf(pdf: jsPDF, item: ITestLabelData): Promise<void> {
    try {
      let currentFontSize = 0;
      let currentFontStyle = '';

      for (const labelItem of this.design.labelItems) {
        const x = labelItem.x / this.mmToPixels;
        const y = labelItem.y / this.mmToPixels;
        const width = labelItem.width / this.mmToPixels;
        const height = labelItem.height / this.mmToPixels;

        // Draw border if showBorder is true
        if (labelItem.showBorder) {
          pdf.setDrawColor(0, 0, 0); // Black border
          pdf.setLineWidth(0.5);
          pdf.rect(x, y, width, height, 'S'); // 'S' for stroke only (border without fill)
        }

        if (labelItem.type === 'image') {
          if (labelItem.isLogo) {
            if (item.logoUrl && this.logoCache.has(item.logoUrl)) {
              const cachedImg = this.logoCache.get(item.logoUrl);
              try {
                let imgData = cachedImg.src;
                if (!imgData.startsWith('data:')) {
                  imgData = await this.imageToDataURL(cachedImg);
                }
                pdf.addImage(imgData, 'JPEG', x, y, width, height, undefined, 'FAST');
              } catch (error) {
                try {
                  let imgData = cachedImg.src;
                  if (!imgData.startsWith('data:')) {
                    imgData = await this.imageToDataURL(cachedImg);
                  }
                  pdf.addImage(imgData, 'JPG', x, y, width, height, undefined, 'FAST');
                } catch {
                  try {
                    let imgData = cachedImg.src;
                    if (!imgData.startsWith('data:')) {
                      imgData = await this.imageToDataURL(cachedImg);
                    }
                    pdf.addImage(imgData, 'PNG', x, y, width, height, undefined, 'FAST');
                  } catch {
                    console.error('Error adding logo to PDF:', error);
                  }
                }
              }
            } else if (labelItem.isLogo && this.logoCache.has('/assets/logos/Build-It-Logo-RED.png')) {
              const cachedImg = this.logoCache.get('/assets/logos/Build-It-Logo-RED.png');
              try {
                let imgData = cachedImg.src;
                if (!imgData.startsWith('data:')) {
                  imgData = await this.imageToDataURL(cachedImg);
                }
                pdf.addImage(imgData, 'JPEG', x, y, width, height, undefined, 'FAST');
              } catch {
                try {
                  let imgData = cachedImg.src;
                  if (!imgData.startsWith('data:')) {
                    imgData = await this.imageToDataURL(cachedImg);
                  }
                  pdf.addImage(imgData, 'JPG', x, y, width, height, undefined, 'FAST');
                } catch {
                  try {
                    let imgData = cachedImg.src;
                    if (!imgData.startsWith('data:')) {
                      imgData = await this.imageToDataURL(cachedImg);
                    }
                    pdf.addImage(imgData, 'PNG', x, y, width, height, undefined, 'FAST');
                  } catch (error) {
                    console.error('Error adding default logo to PDF:', error);
                  }
                }
              }
            } else {
              if (currentFontSize !== 10) {
                pdf.setFontSize(10);
                currentFontSize = 10;
              }
              pdf.text('LOGO', x + width / 2, y + height / 2, {align: 'center'});
            }
          } else if (labelItem.value.includes('{{barcodeGraphic}}') && item.barcode) {
            if (this.barcodeCache.has(item.barcode)) {
              const barcodeData = this.barcodeCache.get(item.barcode);

              try {
                pdf.setFillColor(255, 255, 255);
                pdf.rect(x, y, width, height, 'F');

                pdf.addImage(barcodeData, 'PNG', x, y, width, height, undefined, 'FAST');
              } catch (error) {
                console.error('Error adding barcode to PDF:', error);
                pdf.setFillColor(255, 255, 255);
                pdf.rect(x, y, width, height, 'F');
                if (currentFontSize !== 14) {
                  pdf.setFontSize(14);
                  currentFontSize = 14;
                }
                if (currentFontStyle !== 'bold') {
                  pdf.setFont('helvetica', 'bold');
                  currentFontStyle = 'bold';
                }
                pdf.text(item.barcode, x + width / 2, y + height / 2, {align: 'center'});
              }
            } else {
              try {
                const barcodeData = await this.generateBarcodeImage(item.barcode);
                pdf.setFillColor(255, 255, 255);
                pdf.rect(x, y, width, height, 'F');

                pdf.addImage(barcodeData, 'PNG', x, y, width, height, undefined, 'FAST');
              } catch (error) {
                console.error('Error generating barcode for PDF:', error);
                pdf.setFillColor(255, 255, 255);
                pdf.rect(x, y, width, height, 'F');
                if (currentFontSize !== 14) {
                  pdf.setFontSize(14);
                  currentFontSize = 14;
                }
                if (currentFontStyle !== 'bold') {
                  pdf.setFont('helvetica', 'bold');
                  currentFontStyle = 'bold';
                }
                pdf.text(item.barcode, x + width / 2, y + height / 2, {align: 'center'});
              }
            }
          }
        } else {
          let content = labelItem.value;
          let fontWeight = '';
          if (content.includes('{{')) {
            content = content.replace(/\{\{(\w+)\}\}/g, (match, placeholder) => {
              let replacementValue = '';
              switch (placeholder) {
                case 'description':
                  fontWeight =
                    this.design.labelItems[this.design.labelItems.findIndex((item: ILabelVariable) => item.id === 'description')]
                      .fontWeight;
                  replacementValue = (item.desc || '').toString();
                  break;
                case 'barcode':
                  fontWeight =
                    this.design.labelItems[this.design.labelItems.findIndex((item: ILabelVariable) => item.id === 'barcode')].fontWeight;
                  replacementValue = (item.barcode || '').toString();
                  break;
                case 'itemCode':
                  fontWeight =
                    this.design.labelItems[this.design.labelItems.findIndex((item: ILabelVariable) => item.id === 'itemCode')].fontWeight;
                  replacementValue = (item.code || '').toString();
                  break;
                case 'price':
                  fontWeight =
                    this.design.labelItems[this.design.labelItems.findIndex((item: ILabelVariable) => item.id === 'price')].fontWeight;
                  replacementValue = `${this.design.currencySymbol ? this.design.currencySymbol : 'R'}${this.formatMoney(item.price || 0)}`;
                  break;
                case 'binLoc':
                  fontWeight =
                    this.design.labelItems[this.design.labelItems.findIndex((item: ILabelVariable) => item.id === 'binLoc')].fontWeight;
                  replacementValue = (item.binL || '').toString();
                  break;
                case 'date':
                  fontWeight =
                    this.design.labelItems[this.design.labelItems.findIndex((item: ILabelVariable) => item.id === 'date')].fontWeight;
                  try {
                    if (item.date) {
                      if (item.date instanceof Date) {
                        replacementValue = item.date.toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit', year: 'numeric'});
                      } else {
                        console.warn('Date is not a Date object, converting');
                        const dateObj = new Date(item.date);
                        replacementValue = dateObj.toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit', year: 'numeric'});
                      }
                    } else {
                      replacementValue = new Date().toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit', year: 'numeric'});
                    }
                  } catch (dateError) {
                    console.error('Error formatting date:', dateError);
                    replacementValue = new Date().toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit', year: 'numeric'});
                  }
                  break;
                case 'logo':
                  replacementValue = 'COMPANY NAME';
                  break;
                case 'suppCode':
                  fontWeight =
                    this.design.labelItems[this.design.labelItems.findIndex((item: ILabelVariable) => item.id === 'suppCode')].fontWeight;
                  replacementValue = (item.suppCode || '').toString();
                  break;
                case 'companyName':
                  fontWeight = this.design.labelItems[this.design.labelItems.findIndex((item: ILabelVariable) => item.id === 'companyName')]?.fontWeight ?? 'normal';
                  replacementValue = (item.companyName || '').toString();
                  break;
                default:
                  replacementValue = '';
              }
              return replacementValue;
            });
          }

          if (content === null || content === undefined) {
            console.warn('Content was null or undefined, setting to empty string');
            content = '';
          }

          content = String(content);

          const processedText = content.trim();
          const newFontStyle = labelItem.fontStyle === 'italic' ? 'italic' : 'normal';
          if (currentFontStyle !== newFontStyle) {
            pdf.setFont('helvetica', newFontStyle);
            currentFontStyle = newFontStyle;
          }

          let fontSize = labelItem.fontSize;

          if (fontSize < 8) {
            fontSize = 8;
          }

          pdf.setFont('helvetica', newFontStyle, fontWeight);

          const maxWidth = width - 2; // Account for margins

          if (labelItem.overflow === 'shrink') {
            if (currentFontSize !== fontSize) {
              pdf.setFontSize(fontSize);
              currentFontSize = fontSize;
            }

            let textWidth = pdf.getTextWidth(processedText);

            if (textWidth > maxWidth) {
              const minFontSize = 6; // Don't go smaller than this
              let low = minFontSize;
              let high = fontSize;

              while (low <= high) {
                const mid = Math.floor((low + high) / 2);
                pdf.setFontSize(mid);
                textWidth = pdf.getTextWidth(processedText);

                if (textWidth <= maxWidth) {
                  low = mid + 1;
                } else {
                  high = mid - 1;
                }
              }

              fontSize = high;
              if (fontSize < minFontSize) fontSize = minFontSize;

              pdf.setFontSize(fontSize);
              currentFontSize = fontSize;
            }
          } else if (currentFontSize !== fontSize) {
            pdf.setFontSize(fontSize);
            currentFontSize = fontSize;
          }

          let textX = x;
          if (labelItem.textAlign === 'center') {
            textX = x + width / 2;
          } else if (labelItem.textAlign === 'right') {
            textX = x + width;
          }

          let textY = y;
          if (labelItem.verticalAlign === 'middle') {
            textY = y + height / 2;
          } else if (labelItem.verticalAlign === 'bottom') {
            textY = y + height;
          }

          if (labelItem.overflow === 'wrap') {
            try {
              const words = processedText.split(' ');
              const lineHeight = fontSize * 0.35;
              const maxLines = Math.floor(height / lineHeight);
              const lines: string[] = [];
              let currentLine = '';

              for (const word of words) {
                const testLine = currentLine + (currentLine ? ' ' : '') + word;
                const testWidth = pdf.getTextWidth(testLine);

                if (testWidth > maxWidth && currentLine !== '') {
                  lines.push(currentLine);
                  currentLine = word;
                } else {
                  currentLine = testLine;
                }
              }

              if (currentLine) {
                lines.push(currentLine);
              }
              const linesToDraw: string[] = lines.slice(0, maxLines);

              let startY = textY;
              const totalTextHeight = linesToDraw.length * lineHeight;
              const fontSizeMM = fontSize * 0.353;

              if (labelItem.verticalAlign === 'middle') {
                startY = y + height / 2 - totalTextHeight / 2 + fontSizeMM * 0.7;
              } else if (labelItem.verticalAlign === 'bottom') {
                startY = y + height - totalTextHeight - fontSizeMM * 0.1;
              } else {
                startY = y + fontSizeMM * 1.5;
              }

              for (let i = 0; i < linesToDraw.length; i++) {
                const lineY = startY + i * lineHeight;
                const lineText = linesToDraw[i];

                try {
                  pdf.text(lineText, textX, lineY, {
                    align: labelItem.textAlign,
                    baseline: 'alphabetic' as const,
                  });
                } catch (error) {
                  console.error(`Error rendering line "${lineText}":`, error);
                  throw error;
                }
              }
            } catch (error) {
              console.error('Error in text wrapping:', error);
              // Try a simpler approach as fallback
              const splitTitle: string = pdf.splitTextToSize(
                processedText, // Use sanitized text instead of content
                labelItem.width,
              );
              pdf.text(splitTitle, textX, textY, {
                align: labelItem.textAlign,
                baseline: labelItem.verticalAlign === 'top' ? 'top' : labelItem.verticalAlign === 'middle' ? 'middle' : 'bottom',
                maxWidth: maxWidth,
              });
            }
          } else {
            // Add text to PDF with alignment for non-wrapped text
            try {
              if (labelItem.verticalAlign === 'top' && labelItem.overflow === 'shrink') {
                const extraPadding = 0.5;
                const fontSizeInMM = fontSize * 0.353;
                textY = y + fontSizeInMM * (1.5 + extraPadding);
              }

              const adjustedY = this.getAdjustedYPosition(textY, labelItem.verticalAlign, fontSize);
              let finalText = processedText.trim();

              const textWidth = pdf.getTextWidth(finalText);
              if (textWidth > maxWidth) {
                let truncatedText = finalText;
                while (truncatedText.length > 3 && pdf.getTextWidth(truncatedText + '...') > maxWidth) {
                  truncatedText = truncatedText.slice(0, -1);
                }
                finalText = truncatedText + '...';
              }

              let textAlign = labelItem.textAlign;
              if (!['left', 'center', 'right', 'justify'].includes(textAlign)) {
                console.warn(`Invalid text alignment: ${textAlign}, defaulting to 'left'`);
                textAlign = 'left';
              }

              const textOptions = {
                align: textAlign as TextAlignType,
                baseline: 'alphabetic' as const,
              };

              pdf.text(finalText, textX, adjustedY, textOptions);
            } catch (error) {
              console.error(`Error rendering text "${processedText}":`, error);
              console.error(
                `Text options: x=${textX}, y=${textY}, fontSize=${fontSize}, align=${labelItem.textAlign}, vAlign=${labelItem.verticalAlign}`,
              );

              try {
                pdf.setTextColor(0, 0, 0); // Black text
                pdf.text(
                  processedText.trim().substring(0, 50), // Use shorter text
                  x + 2, // Simple positioning with margins
                  y + 5,
                  {align: 'left'}, // Only use left alignment
                );
              } catch (fallbackError) {
                console.error('Even fallback text rendering failed:', fallbackError);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error drawing label to PDF:', error);
      console.error('Label item that caused error:', JSON.stringify(item));

      try {
        const x = 10;
        const y = 10;

        pdf.setDrawColor(255, 0, 0); // Red
        pdf.setLineWidth(0.5);
        pdf.line(x, y, x + 50, y + 20);
        pdf.line(x + 50, y, x, y + 20);

        pdf.setTextColor(255, 0, 0);
        pdf.setFontSize(8);
        pdf.text('Error rendering label', x, y + 25);
      } catch (fallbackError) {
        console.error('Failed to indicate error in PDF:', fallbackError);
      }
    }
  }

  private imageToDataURL(img: HTMLImageElement): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        if (img.src.startsWith('data:')) {
          resolve(img.src);
          return;
        }

        const canvas = document.createElement('canvas');
        const MAX_DIMENSION = 300;
        const aspectRatio = img.width / img.height;

        if (img.width > MAX_DIMENSION || img.height > MAX_DIMENSION) {
          if (img.width > img.height) {
            canvas.width = MAX_DIMENSION;
            canvas.height = Math.round(MAX_DIMENSION / aspectRatio);
          } else {
            canvas.height = MAX_DIMENSION;
            canvas.width = Math.round(MAX_DIMENSION * aspectRatio);
          }
        } else {
          canvas.width = img.width;
          canvas.height = img.height;
        }

        const ctx = canvas.getContext('2d', {alpha: false});
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'medium';
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const isPhoto = img.src.match(/\.(jpg|jpeg)$/i);
        const dataUrl = canvas.toDataURL(isPhoto ? 'image/jpeg' : 'image/png', 0.8);
        resolve(dataUrl);
      } catch (error) {
        reject(error);
      }
    });
  }

  private async preCacheResources(items: ITestLabelData[]): Promise<void> {
    const uniqueBarcodes = [...new Set(items.map((item) => item.barcode).filter(Boolean))];
    const uniqueLogos = [...new Set(items.filter((item) => item.logoUrl && item.logoUrl.length > 0).map((item) => item.logoUrl))];

    const hasWorker = typeof Worker !== 'undefined';

    const generateBarcodesInBatches = async (barcodes: string[]): Promise<void> => {
      const BARCODE_BATCH_SIZE = 100;

      if (barcodes.length === 0) return;

      for (let i = 0; i < barcodes.length; i += BARCODE_BATCH_SIZE) {
        const batch = barcodes.slice(i, i + BARCODE_BATCH_SIZE);

        // Generate all barcodes in this batch in parallel with Promise.all
        await Promise.all(
          batch.map(async (barcode) => {
            if (!this.barcodeCache.has(barcode)) {
              try {
                const dataUrl = await this.generateBarcodeImage(barcode);
                this.barcodeCache.set(barcode, dataUrl);
              } catch (error) {
                console.error(`Failed to generate barcode: ${barcode}`, error);
              }
            }
          }),
        );
      }
    };

    const loadAllLogos = async (): Promise<void> => {
      const MAX_CONCURRENT_LOADS = 5;
      const logoQueue = [...uniqueLogos];
      if (logoQueue.length === 0) return;

      while (logoQueue.length > 0) {
        const chunk = logoQueue.splice(0, MAX_CONCURRENT_LOADS);
        await Promise.all(
          chunk.map(async (url) => {
            if (!this.logoCache.has(url)) {
              try {
                const img = await this.loadLogoImage(url);
                this.logoCache.set(url, img);
              } catch (error) {
                console.error(`Failed to load logo: ${url}`, error);
              }
            }
          }),
        );
      }

      const hasLogoItem = this.design.labelItems.some((item) => item.isLogo === true);
      if (uniqueLogos.length > 0 && hasLogoItem && !this.logoCache.has('/assets/logos/Build-It-Logo-RED.png')) {
        try {
          const defaultLogo = await this.loadLogoImage('/assets/logos/Build-It-Logo-RED.png');
          this.logoCache.set('/assets/logos/Build-It-Logo-RED.png', defaultLogo);
        } catch (error) {
          console.error('Failed to load default logo', error);
        }
      }
    };

    await Promise.all([generateBarcodesInBatches(uniqueBarcodes), loadAllLogos()]);
  }

  private async loadLogoImage(url: string): Promise<HTMLImageElement> {
    if (this.logoCache.has(url)) {
      return this.logoCache.get(url);
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    const loadWithTimeout = async (src: string, timeoutMs = 2000): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error(`Image load timeout: ${src}`));
        }, timeoutMs);

        img.onload = (): void => {
          clearTimeout(timeout);
          resolve();
        };

        img.onerror = (): void => {
          clearTimeout(timeout);
          img.src = src;
          reject(new Error(`Failed to load image: ${src}`));
        };

        img.src = src;
      });
    };

    try {
      if (url.startsWith('data:')) {
        await loadWithTimeout(url);
      } else {
        const cacheKey = `logo_cache_${url}`;
        const cachedDataUrl = sessionStorage.getItem(cacheKey);

        if (cachedDataUrl) {
          await loadWithTimeout(cachedDataUrl);
        } else {
          const dataUrl = await this.loadImage(url);

          try {
            sessionStorage.setItem(cacheKey, dataUrl);
          } catch (e) {}

          await loadWithTimeout(dataUrl);
        }
      }

      this.logoCache.set(url, img);
      return img;
    } catch (error) {
      console.error(`Error loading logo from ${url}:`, error);

      const placeholderImg = new Image();
      placeholderImg.width = 100;
      placeholderImg.height = 50;

      const canvas = document.createElement('canvas');
      canvas.width = 100;
      canvas.height = 50;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 100, 50);
      ctx.fillStyle = 'black';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('LOGO', 50, 25);

      placeholderImg.src = canvas.toDataURL('image/jpeg', 0.8);
      this.logoCache.set(url, placeholderImg);

      return placeholderImg;
    }
  }

  private loadImage = async (url: string): Promise<string> => {
    try {
      if (url.startsWith('data:')) {
        return url;
      }

      const absoluteUrl = url.startsWith('/') ? `${window.location.origin}${url}` : url;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 3000);

      const response = await fetch(absoluteUrl, {
        signal: controller.signal,
        cache: 'force-cache', // Use browser cache aggressively
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }

      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (): void => {
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error in loadImage:', error);

      const canvas = document.createElement('canvas');
      canvas.width = 100;
      canvas.height = 50;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 100, 50);
      ctx.fillStyle = 'black';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('LOGO', 50, 25);

      return canvas.toDataURL('image/jpeg', 0.8);
    }
  };

  private formatMoney(amount: number): string {
    if (amount === null) console.warn('formatMoney received NULL');
    if (amount === undefined) console.warn('formatMoney received UNDEFINED');
    if (Number.isNaN(amount)) console.warn('formatMoney received NaN');

    if (amount === null || amount === undefined || isNaN(amount)) {
      amount = 0;
    }

    const numAmount = Number(amount);

    return numAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  private async generateBarcodeImage(value: string, width: number = 200, height: number = 80): Promise<string> {
    if (this.barcodeCache.has(value)) {
      return this.barcodeCache.get(value);
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    JsBarcode(canvas, value, {
      format: 'CODE128',
      width: 1,
      height: height,
      displayValue: false,
      margin: 0,
      flat: true,
    });

    const dataUrl = canvas.toDataURL('image/png');
    this.barcodeCache.set(value, dataUrl);
    return dataUrl;
  }

  private getAdjustedYPosition(baseY: number, verticalAlign: string, fontSize: number): number {
    const fontSizeMM = fontSize * 0.353;

    let adjustedY = baseY;

    if (verticalAlign === 'top') {
      adjustedY = baseY + fontSizeMM * 1.5;
    } else if (verticalAlign === 'middle') {
      adjustedY = baseY + fontSizeMM * 0.7;
    } else if (verticalAlign === 'bottom') {
      adjustedY = baseY - fontSizeMM * 0.1;
    }

    return adjustedY;
  }
}
